import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { Book, Comment, Discussion } from "@pagemates/shared";
import { api, describeError } from "../../lib/api";
import { Avatar, Empty, Spinner, useSheetDrag, EmptyState } from "../../components/ui";
import { BackIcon, CloseIcon, NoteIcon } from "../../components/Icons";
import { Composer, type ComposerHandle } from "../../components/Composer";
import { BookPicker } from "../../components/BookPicker";
import { Message, dayLabel, dayOf } from "../../components/Discussion";
import { posLabel } from "../../lib/format";
import { useClub } from "./ClubLayout";
import { PickBook } from "../../components/PickBook";
import { Envelope, LeaveNoteSheet, PredictionSheet } from "../../components/Moments";

export function TalkTab() {
  const { clubId, club } = useClub();
  const c = club.data!;
  const navigate = useNavigate();
  const { thread, book: pickedBook, at } = useSearch({ from: "/authed/clubs/$clubId/talk" });
  const books = useQuery({ queryKey: ["books", clubId], queryFn: () => api.clubBooks(clubId) });
  if (books.isPending) return <main className="page"><Spinner /></main>;
  if (books.isError) return <main className="page"><p className="error">{describeError(books.error)}</p></main>;

  const options = [...(books.data.current ? [books.data.current] : []), ...books.data.finished];
  const book = options.find((b) => b.id === pickedBook) ?? options[0];
  if (!book) {
    return (
      <main className="page">
        <TalkEmpty clubId={clubId} isAdmin={c.myRole !== "member"} />
      </main>
    );
  }
  const setThread = (id: string | null) => void navigate({ to: "/clubs/$clubId/talk", params: { clubId }, search: { ...(pickedBook ? { book: pickedBook } : {}), ...(id ? { thread: id } : {}) }, replace: true });

  return (
    <Stream key={book.id} clubId={clubId} bookId={book.id} book={book.book} isAdmin={c.myRole !== "member"} finished={book.status === "finished"}
      threadId={thread ?? null} setThread={setThread} at={at ?? null} current={book.status === "current"} plan={book.plan?.landmarks ?? []}
      picker={<BookPicker books={options} current={book} onPick={(id) => void navigate({ to: "/clubs/$clubId/talk", params: { clubId }, search: { book: id } })} />} />
  );
}

/** More opened than this and only the first few unfold; the rest show as usual. */
const UNFOLD_ALL_UP_TO = 20, UNFOLD_FIRST = 3;

function Stream({ clubId, bookId, book, isAdmin, finished, threadId, setThread, picker, at, current, plan }:
  { clubId: string; bookId: string; book: Book; isAdmin: boolean; finished: boolean; threadId: string | null; setThread: (id: string | null) => void; picker: React.ReactNode;
    at: string | null; current: boolean; plan: { name: string; percent: number }[] }) {
  const title = book.title;
  // Opening Talk counts as opening the app: work out the unsealing moment first, so what it opened is marked.
  const moment = useQuery({ queryKey: ["moment", clubId, bookId, "talk"], queryFn: () => api.moment(clubId, bookId).catch(() => null), staleTime: Infinity });
  const d = useQuery({
    queryKey: ["discussion", clubId, bookId], queryFn: () => api.discussion(clubId, bookId),
    refetchInterval: 15_000, refetchOnWindowFocus: true, enabled: moment.isFetched,
  });
  // "New" markers stay put for the whole visit even as the query refetches.
  const newIds = useRef(new Set<string>());
  // "Just opened" stays for the whole visit too: looking at Talk tells the server, which then stops marking them.
  const openedIds = useRef<string[]>([]);
  const [sheet, setSheet] = useState<"note" | "predict" | null>(null);
  const preds = useQuery({ queryKey: ["predictions", clubId, bookId, "talk"], queryFn: () => api.predictions(clubId, bookId), enabled: current });
  // Ids seen on the first render: later arrivals get the "arrive" animation.
  const seenIds = useRef<Set<string> | null>(null);
  const streamRef = useRef<HTMLDivElement>(null);
  const [stickBottom, setStickBottom] = useState(true);

  useEffect(() => {
    if (!d.data) return;
    const walk = (cs: Comment[]) => { for (const c of cs) { if (c.isNew) newIds.current.add(c.id); if (c.justOpened && !openedIds.current.includes(c.id)) openedIds.current.push(c.id); walk(c.replies); } };
    walk(d.data.comments);
  }, [d.data]);
  // From the moment card: start at the first message that opened, not at the bottom.
  const jumped = useRef(false);
  useEffect(() => {
    if (!at || jumped.current || !d.data) return;
    const el = document.getElementById(`m-${at}`);
    if (!el) return;
    jumped.current = true;
    setStickBottom(false);
    requestAnimationFrame(() => el.scrollIntoView({ block: "center" }));
  }, [at, d.data]);
  // Tell the server we looked, once on open and again when leaving.
  // Only once the messages are in, so "Just opened" is read before looking clears it.
  const loaded = d.isSuccess;
  useEffect(() => {
    if (!loaded) return;
    const t = setTimeout(() => void api.markSeen(clubId, bookId).catch(() => undefined), 1500);
    return () => { clearTimeout(t); void api.markSeen(clubId, bookId).catch(() => undefined); };
  }, [clubId, bookId, loaded]);
  // Keep the stream pinned to the bottom unless the reader scrolled up.
  useEffect(() => {
    const el = streamRef.current;
    if (el && stickBottom) el.scrollTop = el.scrollHeight;
  }, [d.data, stickBottom]);
  // ...including when the keyboard opens or the composer grows a strip.
  useEffect(() => {
    const pin = () => { const el = streamRef.current; if (el && stickBottom) requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; }); };
    window.visualViewport?.addEventListener("resize", pin);
    const ro = new ResizeObserver(pin);
    const composer = streamRef.current?.parentElement?.querySelector(":scope > .composer");
    if (composer) ro.observe(composer);
    return () => { window.visualViewport?.removeEventListener("resize", pin); ro.disconnect(); };
  }, [stickBottom]);
  const onScroll = () => {
    const el = streamRef.current;
    if (el) setStickBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 40);
  };

  const names = useMemo(() => {
    const out = new Set<string>();
    const walk = (cs: Comment[]) => { for (const c of cs) { out.add(c.author.name); walk(c.replies); } };
    if (d.data) walk(d.data.comments);
    return [...out];
  }, [d.data]);

  if (d.isPending) return <main className="page"><Spinner /></main>;
  if (d.isError) return <main className="page"><p className="error">{describeError(d.error)}</p></main>;
  const t = d.data;
  const open = threadId ? t.comments.find((c) => c.id === threadId) ?? null : null;
  // Latched on first render too, so the labels are there for the first paint.
  if (openedIds.current.length === 0) { const walk = (cs: Comment[]) => { for (const c of cs) { if (c.justOpened) openedIds.current.push(c.id); walk(c.replies); } }; walk(t.comments); }
  const unfolds = new Set(openedIds.current.length > UNFOLD_ALL_UP_TO ? openedIds.current.slice(0, UNFOLD_FIRST) : openedIds.current);
  const justOpened = (c: Comment) => unfolds.has(c.id);
  const isNew = (c: Comment) => !justOpened(c) && (c.isNew || newIds.current.has(c.id)); // "Just opened" wins
  const ctx = { ...t, landmarks: t.landmarks };
  const firstNew = t.comments.find((c) => isNew(c))?.id;
  if (seenIds.current === null) seenIds.current = new Set(t.comments.map((c) => c.id));
  const arrived = (id: string) => !seenIds.current!.has(id);

  return (
    <div className={`talk${open ? " with-thread" : ""}`} style={book.coverTone ? { "--tone": book.coverTone } as React.CSSProperties : undefined}>
      <div className="talk-book">
        {book.coverUrl && <div className="art" aria-hidden="true"><img src={book.coverUrl} alt="" /></div>}
        <div className="t">{picker ?? <b>{title}</b>}
          <span className="bp-side">
            <Link to="/clubs/$clubId/notes/$bookId" params={{ clubId, bookId }} className="nb-link" title="Your private notes on this book" aria-label="Your notes"><NoteIcon width={16} height={16} /></Link>
            {finished ? <Link to="/clubs/$clubId/shelf" params={{ clubId }} className="bp-you back-shelf"><BackIcon width={14} height={14} />Shelf</Link>
              : <span className="bp-you">{t.gating ? <>you're at <strong className="num">{posLabel(t.myProgress, { ...t, landmarks: [] }).replace("open", "0%")}</strong></> : "gating off"}</span>}
          </span></div>
        <div className="bar"><i style={{ width: `${t.myProgress}%` }} />{t.landmarks.map((l) => <span key={l.id} className="mark" style={{ left: `${l.percent}%` }} title={l.name} />)}</div>
      </div>

      <div className="stream" ref={streamRef} onScroll={onScroll}>
        {t.comments.length === 0 && (
          <Empty title="Nobody has said anything yet">Say where you are, or set the chip to spoiler-free for the small talk.</Empty>
        )}
        {t.comments.map((c, i) => {
          const prev = t.comments[i - 1];
          const showDay = !prev || dayOf(prev.createdAt) !== dayOf(c.createdAt);
          const showPos = !c.locked && c.position > 0 && (!prev || prev.locked || prev.position !== c.position);
          return (
            <FragmentRow key={c.id}>
              {showDay && <div className="day">{dayLabel(c.createdAt)}</div>}
              {c.id === firstNew && (
                <div className="day new">New since your last visit</div>
              )}
              <Message clubId={clubId} bookId={bookId} c={c} d={t} names={names} isAdmin={isAdmin} showPos={showPos} isNew={isNew(c)}
                arrive={arrived(c.id)} onOpenThread={(id) => setThread(id)} justOpened={justOpened(c)} />
            </FragmentRow>
          );
        })}
      </div>

      <Composer clubId={clubId} bookId={bookId} herePercent={t.myProgress} hereLabel="Where I am" placeholder="Message the club" ahead={t.ahead} ctx={t}
        onPosted={() => setStickBottom(true)}
        extras={[
          ...(current && t.myProgress > 0 && (t.myStatus === "reading" || t.myStatus === "finished") ? [{ key: "note", label: "Leave a note forward", about: "It waits at a page for whoever gets there after you.", icon: <Envelope />, onPick: () => setSheet("note") }] : []),
          ...(preds.data?.canWrite ? [{ key: "predict", label: "Make a prediction", about: "Sealed until each reader finishes.", icon: <Envelope />, onPick: () => setSheet("predict") }] : []),
        ]} />
      {sheet === "note" && <LeaveNoteSheet clubId={clubId} bookId={bookId} ctx={ctx} myPercent={t.myStatus === "finished" ? 100 : t.myProgress} onClose={() => setSheet(null)} />}
      {sheet === "predict" && <PredictionSheet clubId={clubId} bookId={bookId} ctx={ctx} onClose={() => setSheet(null)} />}

      {open && (
        <>
          <div className="thread-dim" onClick={() => setThread(null)} />
          <ThreadPanel clubId={clubId} bookId={bookId} parent={open} d={t} names={names} isAdmin={isAdmin} isNew={isNew} onClose={() => setThread(null)} />
        </>
      )}
    </div>
  );
}

function FragmentRow({ children }: { children: React.ReactNode }) { return <>{children}</>; }

function ThreadPanel({ clubId, bookId, parent, d, names, isAdmin, isNew, onClose }:
  { clubId: string; bookId: string; parent: Comment; d: Discussion; names: string[]; isAdmin: boolean; isNew: (c: Comment) => boolean; onClose: () => void }) {
  const handle = useRef<ComposerHandle | null>(null);
  const register = useCallback((h: ComposerHandle) => { handle.current = h; }, []);
  const replyTo = (c: Comment) => handle.current?.replyTo(c.author.name);
  const isQ = parent.kind === "question";
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => { const el = listRef.current; if (el) el.scrollTop = el.scrollHeight; }, [parent.replies.length]);

  const sheetRef = useRef<HTMLElement>(null);
  useSheetDrag(sheetRef, onClose);
  return (
    <aside className="thread" aria-label="Thread" ref={sheetRef}>
      <div className="grab" />
      <div className="head"><b>{isQ ? (parent.mine ? "Your question" : "Question") : "Thread"}</b><button type="button" aria-label="Close thread" onClick={onClose}><CloseIcon /></button></div>
      <div className="parent">
        <Message clubId={clubId} bookId={bookId} c={parent} d={d} names={names} isAdmin={isAdmin} showPos isNew={false} />
      </div>
      <div className="list" ref={listRef}>
        {parent.kind === "finishline" && d.finishLine && (
          <div className="reactions">
            <div className="eyebrow">First reactions</div>
            {d.finishLine.reactions.length === 0 && <p className="muted small">Finish the book to see them.</p>}
            {d.finishLine.reactions.map((r) => (
              <div key={r.id} className="row" style={{ alignItems: "flex-start" }}>
                <Avatar name={r.name} image={r.image} />
                <div style={{ minWidth: 0 }}>
                  <div className="small muted">{r.name}{r.status === "dnf" ? ` · stopped at ${r.percent}%` : ""} · {new Date(r.at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>
                  <div className="book">{r.reaction ? `“${r.reaction}”` : <span className="muted small">arrived without a word</span>}</div>
                </div>
              </div>
            ))}
          </div>
        )}
        {parent.replies.length === 0 && parent.kind !== "finishline" && <p className="muted small">{isQ ? "Nobody has answered yet." : "No replies yet."}</p>}
        {parent.replies.map((r, i) => {
          const prev = parent.replies[i - 1];
          const showPos = !r.locked && r.position !== parent.position && (isQ ? r.mine : (!prev || prev.position !== r.position));
          return <Message key={r.id} clubId={clubId} bookId={bookId} c={r} d={d} names={names} isAdmin={isAdmin} showPos={showPos} isNew={isNew(r)} onReplyTo={replyTo} inQuestion={isQ} canReveal={isQ && parent.mine && d.earlyReveal} justOpened={r.justOpened} />;
        })}
      </div>
      <Composer clubId={clubId} bookId={bookId} herePercent={parent.position} hereLabel="This thread" parentId={parent.id} placeholder={isQ ? "Reply" : "Reply in thread"} register={register}
        question={isQ ? { asker: parent.author.name, position: parent.position, mine: parent.mine } : undefined} myProgress={d.myProgress} myDone={d.myStatus !== "reading"} ctx={d} />
    </aside>
  );
}

/** No book yet: the discussion waits for one. Admins pick it from here; members go to the Shelf. */
function TalkEmpty({ clubId, isAdmin }: { clubId: string; isAdmin: boolean }) {
  const [picking, setPicking] = useState(false);
  return (
    <>
      <EmptyState title="The discussion opens with the first book"
        action={isAdmin
          ? <button type="button" className="btn primary" onClick={() => setPicking(true)}>Pick the first book</button>
          : <Link to="/clubs/$clubId/shelf" params={{ clubId }} className="btn primary">Go to the Shelf</Link>}
        secondary={<Link to="/clubs/$clubId/club" params={{ clubId }}>Say hi on the noticeboard</Link>}>
        Messages are tied to where you are in the book. Anything past your spot stays sealed until you get there.
      </EmptyState>
      {picking && <PickBook clubId={clubId} mode="current" first onClose={() => setPicking(false)} />}
    </>
  );
}
